import { http } from '@/core/api'
import type { SupplierZaloNotifyPrefs } from '@/features/channel-suppliers/api/supplier-units.api'

export type RecruitmentPortalAccessScope = 'all' | 'team' | 'user'
export type RecruitmentPortalAccessDepartment = 'recruitment' | 'onsite'
export type RecruitmentPortalZaloNotifyChannel = 'gmf' | 'bot' | 'personal'

export type RecruitmentPortalZaloPersonalLink = {
  group_id: string
  name: string
  groups: Array<{ id: string; name: string }>
}

export type RecruitmentPortalAccessQrRow = {
  id: number
  name: string
  logo_url?: string | null
  token: string
  department_code: RecruitmentPortalAccessDepartment
  scope_type: RecruitmentPortalAccessScope
  team_id: number | null
  team_name?: string | null
  user_id: number | null
  user_name?: string | null
  user_employee_code?: string | null
  is_enabled: boolean
  login_url: string
  intake_cutoff_time?: string | null
  intake_notify_enabled?: boolean
  intake_zalo_notify_prefs?: SupplierZaloNotifyPrefs | null
  intake_zalo_notify_channel?: RecruitmentPortalZaloNotifyChannel | null
  intake_zalo_bot_id?: number | null
  intake_zalo_bot_chat_id?: string | null
  intake_zalo_bot_label?: string | null
  intake_zalo_personal_group_ids?: string[]
  intake_zalo_personal?: RecruitmentPortalZaloPersonalLink | null
  intake_zalo_group_id?: string | null
  intake_zalo_group_name?: string | null
  intake_zalo_group_tier?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type RecruitmentPortalAccessQrPayload = {
  name: string
  department_code: RecruitmentPortalAccessDepartment
  scope_type: RecruitmentPortalAccessScope
  team_id?: number | null
  user_id?: number | null
  is_enabled?: boolean
  intake_cutoff_time?: string | null
  intake_notify_enabled?: boolean
  intake_zalo_notify_prefs?: SupplierZaloNotifyPrefs | null
  intake_zalo_notify_channel?: RecruitmentPortalZaloNotifyChannel | null
  intake_zalo_bot_id?: number | null
  intake_zalo_bot_chat_id?: string | null
  intake_zalo_personal_group_ids?: string[]
  intake_zalo_personal_groups?: Array<{ id: string; name: string }>
  intake_zalo_group_id?: string | null
  intake_zalo_group_name?: string | null
  intake_zalo_group_tier?: string | null
}

export type RecruitmentPortalAccessQrUpdatePayload = {
  name?: string
  is_enabled?: boolean
  rotate_token?: boolean
  intake_cutoff_time?: string | null
  intake_notify_enabled?: boolean
  intake_zalo_notify_prefs?: SupplierZaloNotifyPrefs | null
  intake_zalo_notify_channel?: RecruitmentPortalZaloNotifyChannel | null
  intake_zalo_bot_id?: number | null
  intake_zalo_bot_chat_id?: string | null
  intake_zalo_personal_group_ids?: string[]
  intake_zalo_personal_groups?: Array<{ id: string; name: string }>
  intake_zalo_group_id?: string | null
  intake_zalo_group_name?: string | null
  intake_zalo_group_tier?: string | null
}

export const recruitmentPortalAccessQrApi = {
  async list(params?: {
    department_code?: string
    scope_type?: string
    is_enabled?: boolean
  }): Promise<{ data: RecruitmentPortalAccessQrRow[] }> {
    const { data } = await http.get<{ data: RecruitmentPortalAccessQrRow[] }>(
      '/recruitment/portal-access-qrs',
      { params },
    )
    return data
  },

  async create(payload: RecruitmentPortalAccessQrPayload): Promise<{ data: RecruitmentPortalAccessQrRow }> {
    const { data } = await http.post<{ data: RecruitmentPortalAccessQrRow }>(
      '/recruitment/portal-access-qrs',
      payload,
    )
    return data
  },

  async update(
    id: number,
    payload: RecruitmentPortalAccessQrUpdatePayload,
  ): Promise<{ data: RecruitmentPortalAccessQrRow }> {
    const { data } = await http.patch<{ data: RecruitmentPortalAccessQrRow }>(
      `/recruitment/portal-access-qrs/${id}`,
      payload,
    )
    return data
  },

  async remove(id: number): Promise<void> {
    await http.delete(`/recruitment/portal-access-qrs/${id}`)
  },

  async uploadLogo(id: number, file: File): Promise<{ data: RecruitmentPortalAccessQrRow }> {
    const fd = new FormData()
    fd.append('logo', file)
    const { data } = await http.post<{ data: RecruitmentPortalAccessQrRow }>(
      `/recruitment/portal-access-qrs/${id}/logo`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return data
  },

  async removeLogo(id: number): Promise<{ data: RecruitmentPortalAccessQrRow }> {
    const { data } = await http.delete<{ data: RecruitmentPortalAccessQrRow }>(
      `/recruitment/portal-access-qrs/${id}/logo`,
    )
    return data
  },
}
